import { ApiError } from '@discovr/common';
import type { OutcomeMessage } from './shopifyEdit';
import { generateReviewCopy } from './shopifyActions';

const codes = (n: number) => `${n} code${n === 1 ? '' : 's'}`;
const discounts = (n: number) => `${n} discount${n === 1 ? '' : 's'}`;
const wereOrWas = (n: number) => (n === 1 ? 'was' : 'were');

const SECTION = 'Changed in Shopify';

/**
 * Copy for a code the merchant renamed in Shopify Admin. Discovr never adopts
 * the new code: the row stays on its own code, is not live, and orders on the
 * new code are not credited (attribution is by the creator's own code only).
 */
export const shopifyRenamedCopy = {
  badge: 'Code not working',
  sectionTitle: SECTION,
  sectionWarning: (n: number) =>
    `${codes(n)} ${wereOrWas(n)} changed in Shopify, so ${n === 1 ? 'it is' : 'they are'} not live. Orders using the new ${n === 1 ? 'code are' : 'codes are'} not credited to the creator.`,
  changedTo: (to: string) => `Changed in Shopify to ${to}`,
  notCredited: (to: string) => `Orders using ${to} are not credited.`,
  notLiveGuidance: 'No content is live yet, so you can change this code in Discovr.',
  notLiveViewOnly: 'No content is live yet, so this code can still be changed in Discovr.',
  liveWarning: (code: string, campaignScope: boolean) =>
    campaignScope
      ? `A creator’s posted content uses ${code}, so the code and link can’t change.`
      : `This creator’s posted content uses ${code}, so the code and link can’t change.`,
  liveActionViewOnly: (code: string) =>
    `${code} needs to be recreated in Shopify Admin (Discounts).`,
  renamedCodeStays: (to: string) =>
    `${to} stays active in Shopify and isn’t credited to anyone. Delete it in Shopify if you don’t want it used.`,
  pickerNote: `Generate skips this creator. See ${SECTION} in the Coupons list.`,
  campaignCardNote: `No code is live for this campaign. A code was changed in Shopify: see “${SECTION}” below.`,
  changeCode: 'Change code',
} as const;

const nonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/** Copy for a 409 SHOPIFY_CAMPAIGN_CODE_RENAMED or SHOPIFY_CREATOR_CODE_RENAMED,
 *  read from `details`; undefined for any other error. */
export function renamedConflictMessage(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const whose =
    err.code === 'SHOPIFY_CAMPAIGN_CODE_RENAMED'
      ? 'This campaign’s'
      : err.code === 'SHOPIFY_CREATOR_CODE_RENAMED'
        ? 'This creator’s'
        : undefined;
  if (!whose) return undefined;
  const details = isRecord(err.details) ? err.details : {};
  if (!nonEmptyString(details.code) || !nonEmptyString(details.renamed_to)) {
    return `${whose} code was changed in Shopify. Fix it under “${SECTION}” before making another code live.`;
  }
  return `${whose} code ${details.code} was changed in Shopify to ${details.renamed_to}. Fix it under “${SECTION}” before making another code live.`;
}

/** Codes named inline in the Generate toast; a longer run is only counted. */
const GENERATE_NAMED_MAX = 5;

/** Why Generate left creators out, as the server counts them. */
export interface GenerateSkipReasons {
  already_live?: number;
  deactivated?: number;
  in_progress?: number;
  on_hold?: number;
}

function skipSummary(by: GenerateSkipReasons, renamed: number): string {
  const parts: [string, number][] = [
    ['already have a code', by.already_live ?? 0],
    ['code deactivated', by.deactivated ?? 0],
    ['code changed in Shopify', renamed],
    ['held creators', by.on_hold ?? 0],
    ['already being created', by.in_progress ?? 0],
  ];
  const shown = parts.filter(([, n]) => n > 0);
  const total = shown.reduce((sum, [, n]) => sum + n, 0);
  if (total === 0) return '';
  return `${total} skipped: ${shown.map(([label, n]) => `${label} (${n})`).join(', ')}`;
}

/** The Generate toast, from the counts and codes the server returned. */
export function generateResultMessage(res: {
  generated: number;
  skipped: number;
  renamed?: number;
  skipped_by?: GenerateSkipReasons;
  created_codes?: string[];
  partial?: boolean;
  remaining?: number;
}): OutcomeMessage {
  const renamed = res.renamed ?? 0;
  const created = res.created_codes;
  if (res.partial && (res.remaining ?? 0) > 0) {
    return {
      kind: 'warning',
      message: generateReviewCopy.partialSummary(
        created?.length ?? res.generated,
        res.remaining ?? 0,
      ),
    };
  }
  if (created) {
    const made =
      created.length === 0
        ? 'No codes created'
        : created.length <= GENERATE_NAMED_MAX
          ? `Created ${codes(created.length)}: ${created.join(', ')}`
          : `${codes(created.length)} created. They’re in the Coupons list`;
    const skips =
      skipSummary(res.skipped_by ?? {}, renamed) ||
      (res.skipped > 0 ? `${res.skipped} skipped` : '');
    const tail = renamed > 0 ? ` See “${SECTION}”.` : '';
    return {
      kind: renamed > 0 || created.length === 0 ? 'warning' : 'success',
      message: `${made}.${skips ? ` ${skips}.` : ''}${tail}`,
    };
  }
  const base = `Generated ${res.generated} code(s), skipped ${res.skipped}`;
  if (renamed === 0) return { kind: 'success', message: base };
  return {
    kind: 'warning',
    message: `${base}. ${codes(renamed)} changed in Shopify ${wereOrWas(renamed)} not recreated. See “${SECTION}”.`,
  };
}

/** The Refresh from Shopify toast, from the counts the server returned. */
export function refreshResultMessage(res: {
  codes_unchecked?: number;
  codes_deferred?: number;
  codes_renamed?: number;
  renames_ambiguous?: number;
}): OutcomeMessage {
  const unchecked = res.codes_unchecked ?? 0;
  const renamed = res.codes_renamed ?? 0;
  const deferred = res.codes_deferred ?? 0;
  const ambiguous = res.renames_ambiguous ?? 0;
  if (unchecked === 0 && renamed === 0 && deferred === 0 && ambiguous === 0) {
    return { kind: 'success', message: 'Discount codes updated from Shopify' };
  }
  const parts = [
    unchecked > 0
      ? `Discount codes updated from Shopify, but codes on ${unchecked} discount${unchecked === 1 ? '' : 's'} couldn’t be checked. Try again later.`
      : 'Discount codes updated from Shopify.',
  ];
  if (renamed > 0) {
    parts.push(
      `${codes(renamed)} ${wereOrWas(renamed)} changed in Shopify and ${renamed === 1 ? 'is' : 'are'} no longer live. See “${SECTION}”.`,
    );
  }
  // renames_ambiguous counts discounts, not codes; the backend removed their missing codes.
  if (ambiguous > 0) {
    parts.push(
      `Codes changed in Shopify on ${discounts(ambiguous)} couldn’t be matched to their creators and were removed. Check ${ambiguous === 1 ? 'the discount' : 'those discounts'} in Shopify.`,
    );
  }
  if (deferred > 0) {
    parts.push(`${codes(deferred)} missing in Shopify will be checked again later.`);
  }
  return { kind: 'warning', message: parts.join(' ') };
}
